import { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { api } from "@/lib/api";
import { Send, Loader2, Download, MessageSquare, X, Plus, ChevronDown, FileSpreadsheet, FileText, CalendarClock, BarChart3, History, Users, Tag, Sparkles } from "lucide-react";
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";
import { ScheduleDialog } from "@/components/chat/ScheduleDialog";
import { ScheduledReports } from "@/components/chat/ScheduledReports";
import { toast } from "sonner";

const SERIES_COLORS = ["#E32C17", "#1F2937"];
const isDateLike = (v) => {
  const s = String(v ?? "").trim();
  return /^\d{4}-\d{2}/.test(s) || /^\d{1,2}[-/]\d{1,2}[-/]\d{2,4}$/.test(s)
    || /^(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)[a-z]*[ '-]?\d{2,4}$/i.test(s);
};

const ChatChart = ({ table }) => {
  const spec = useMemo(() => {
    let numCols = table.columns.filter((c) => c.fmt === "num" || c.fmt === "int").slice(0, 2);
    const labelCol = table.columns.find((c) => c.fmt !== "num" && c.fmt !== "int");
    if (!numCols.length || !labelCol || table.rows.length < 2) return null;
    if (numCols.length === 2) {
      const max = (c) => Math.max(...table.rows.map((r) => Math.abs(Number(r[c.key]) || 0)));
      const m1 = max(numCols[0]), m2 = max(numCols[1]);
      if (m1 && m2 && (m2 / m1 < 0.02 || m1 / m2 < 0.02)) numCols = [m1 >= m2 ? numCols[0] : numCols[1]];
    }
    const trend = table.rows.length >= 3 && table.rows.slice(0, 5).every((r) => isDateLike(r[labelCol.key]));
    const src = trend ? table.rows : [...table.rows].sort((a, b) => (Number(b[numCols[0].key]) || 0) - (Number(a[numCols[0].key]) || 0));
    const data = src.slice(0, 12).map((r) => ({
      label: String(r[labelCol.key] ?? "").slice(0, 20),
      ...Object.fromEntries(numCols.map((c) => [c.label, Number(r[c.key]) || 0])),
    }));
    return { data, trend, numCols, truncated: !trend && table.rows.length > 12, sortLabel: numCols[0].label };
  }, [table]);
  if (!spec) return null;
  const axes = (
    <>
      <CartesianGrid strokeDasharray="3 3" stroke="#F1F3F5" vertical={false} />
      <XAxis dataKey="label" tick={{ fontSize: 8 }} interval={0} angle={-25} textAnchor="end" height={45} />
      <YAxis tick={{ fontSize: 8 }} width={45} tickFormatter={(v) => (Math.abs(v) >= 1000 ? `${(v / 1000).toFixed(0)}k` : v)} />
      <Tooltip contentStyle={{ fontSize: 10 }} formatter={(v) => Number(v).toLocaleString("en-IN")} />
      {spec.numCols.length > 1 && <Legend wrapperStyle={{ fontSize: 9 }} />}
    </>
  );
  return (
    <div className="px-2 pt-2 bg-white border-b border-[#F1F3F5]" data-testid="chat-chart">
      <ResponsiveContainer width="100%" height={175}>
        {spec.trend ? (
          <LineChart data={spec.data} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
            {axes}
            {spec.numCols.map((c, i) => (
              <Line key={c.key} type="monotone" dataKey={c.label} stroke={SERIES_COLORS[i]} strokeWidth={2} dot={{ r: 2 }} />
            ))}
          </LineChart>
        ) : (
          <BarChart data={spec.data} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
            {axes}
            {spec.numCols.map((c, i) => (
              <Bar key={c.key} dataKey={c.label} fill={SERIES_COLORS[i]} radius={[2, 2, 0, 0]} maxBarSize={28} />
            ))}
          </BarChart>
        )}
      </ResponsiveContainer>
      {spec.truncated && (
        <div className="text-[8px] text-[#9CA3AF] pb-1">Top 12 of {table.total_rows} by {spec.sortLabel}</div>
      )}
    </div>
  );
};

const chartable = (table) => table.rows.length >= 2
  && table.columns.some((c) => c.fmt === "num" || c.fmt === "int")
  && table.columns.some((c) => c.fmt !== "num" && c.fmt !== "int");

const parseMdTable = (content) => {
  const lines = (content || "").split("\n");
  let start = -1;
  for (let i = 0; i < lines.length - 1; i++) {
    if (lines[i].trim().startsWith("|") && /^\|[\s:|-]+\|?$/.test(lines[i + 1].trim())) { start = i; break; }
  }
  if (start < 0) return null;
  const cells = (l) => l.trim().replace(/^\|/, "").replace(/\|$/, "").split("|").map((c) => c.trim());
  const header = cells(lines[start]);
  const raw = [];
  for (let i = start + 2; i < lines.length; i++) {
    const t = lines[i].trim();
    if (!t.startsWith("|")) break;
    const c = cells(t);
    if (c.length === header.length) raw.push(c);
  }
  if (raw.length < 2 || header.length < 2) return null;
  const isNum = (s) => s !== "" && /^-?[\d,]+(\.\d+)?$/.test(String(s).replace(/\s/g, ""));
  const columns = header.map((h, idx) => ({
    key: String(idx), label: h,
    fmt: raw.some((r) => isNum(r[idx])) && raw.every((r) => isNum(r[idx]) || r[idx] === "" || r[idx] === "-") ? "num" : "str",
  }));
  const rows = raw.map((r) => Object.fromEntries(r.map((v, idx) =>
    [String(idx), columns[idx].fmt === "num" ? Number(String(v).replace(/,/g, "")) || 0 : v])));
  return { columns, rows, total_rows: rows.length };
};

const MdAnswerChart = ({ content }) => {
  const [show, setShow] = useState(true);
  const table = useMemo(() => parseMdTable(content), [content]);
  if (!table || !chartable(table)) return null;
  return (
    <div className="mt-2 border border-[#E2E5EA] rounded-sm overflow-hidden bg-white" data-testid="chat-md-chart">
      <div className="flex items-center gap-2 px-2 py-1 bg-[#F1F3F5] border-b border-[#E2E5EA]">
        <span className="text-[9px] font-bold uppercase text-[#6B7280]">Chart — from the table above</span>
        <button onClick={() => setShow(!show)} data-testid="chat-md-chart-toggle"
          className={`ml-auto flex items-center gap-1 text-[9px] px-1.5 py-0.5 rounded-sm font-bold border ${show ? "border-[#E32C17] bg-red-50 text-[#E32C17]" : "border-[#E2E5EA] bg-white text-[#6B7280] hover:border-[#E32C17]"}`}>
          <BarChart3 size={9} /> {show ? "Hide" : "Show"}
        </button>
      </div>
      {show && <ChatChart table={table} />}
    </div>
  );
};

const ChatTable = ({ table, onDownload, onSchedule }) => {
  const [showChart, setShowChart] = useState(true);
  const canChart = chartable(table);
  return (
  <div className="mt-2 border border-[#E2E5EA] rounded-sm overflow-hidden bg-white" data-testid="chat-table">
    <div className="flex items-center gap-2 px-2 py-1.5 bg-[#F1F3F5] border-b border-[#E2E5EA] flex-wrap">
      <span className="text-[10px] font-bold uppercase text-[#1A1D21]">{table.title}</span>
      <span className="text-[9px] text-[#9CA3AF]">{table.total_rows} row(s)</span>
      {canChart && (
        <button onClick={() => setShowChart(!showChart)} data-testid="chat-chart-toggle" title="Toggle chart view"
          className={`ml-auto flex items-center gap-1 text-[9px] px-1.5 py-0.5 rounded-sm font-bold border ${showChart ? "border-[#E32C17] bg-red-50 text-[#E32C17]" : "border-[#E2E5EA] bg-white text-[#6B7280] hover:border-[#E32C17]"}`}>
          <BarChart3 size={9} /> Chart
        </button>
      )}
      <button onClick={() => onDownload(table.report_id, "xlsx", table.title)} data-testid="chat-dl-xlsx"
        className={`${canChart ? "" : "ml-auto "}flex items-center gap-1 text-[9px] px-1.5 py-0.5 rounded-sm font-bold border border-emerald-300 bg-emerald-50 text-emerald-700 hover:bg-emerald-100`}>
        <FileSpreadsheet size={9} /> Excel
      </button>
      <button onClick={() => onDownload(table.report_id, "pdf", table.title)} data-testid="chat-dl-pdf"
        className="flex items-center gap-1 text-[9px] px-1.5 py-0.5 rounded-sm font-bold border border-red-300 bg-red-50 text-red-700 hover:bg-red-100">
        <FileText size={9} /> PDF
      </button>
      <button onClick={() => onSchedule(table)} data-testid="chat-schedule-btn" title="Email this report on a schedule"
        className="flex items-center gap-1 text-[9px] px-1.5 py-0.5 rounded-sm font-bold border border-blue-300 bg-blue-50 text-blue-700 hover:bg-blue-100">
        <CalendarClock size={9} /> Schedule
      </button>
    </div>
    {canChart && showChart && <ChatChart table={table} />}
    <div className="max-h-60 overflow-auto">
      <table className="w-full text-[9px]">
        <thead className="sticky top-0 bg-[#1A1D21] text-white">
          <tr>{table.columns.map((c) => <th key={c.key} className={`py-1 px-1.5 whitespace-nowrap ${c.fmt === "num" || c.fmt === "int" ? "text-right" : "text-left"}`}>{c.label}</th>)}</tr>
        </thead>
        <tbody>
          {table.rows.slice(0, 25).map((r, i) => (
            <tr key={i} className="border-b border-[#F1F3F5]">
              {table.columns.map((c) => (
                <td key={c.key} className={`py-0.5 px-1.5 whitespace-nowrap max-w-[180px] truncate ${c.fmt === "num" || c.fmt === "int" ? "text-right font-mono" : ""}`}>
                  {c.fmt === "num" || c.fmt === "int" ? Number(r[c.key] || 0).toLocaleString("en-IN") : (r[c.key] ?? "")}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
    {table.total_rows > 25 && (
      <div className="text-[8px] text-[#9CA3AF] px-2 py-1 border-t border-[#F1F3F5]">Showing 25 of {table.total_rows} — download Excel/PDF for the full data</div>
    )}
  </div>
  );
};

export function ChatPanel({ isOpen, onClose, isFullPage = false }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingStatus, setLoadingStatus] = useState("");
  const [sessionId, setSessionId] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [showSessions, setShowSessions] = useState(false);
  const [showSchedules, setShowSchedules] = useState(false);
  const [scheduleFor, setScheduleFor] = useState(null);
  const bottomRef = useRef(null);
  const inputRef = useRef(null);
  const [suggests, setSuggests] = useState([]);
  const [sugActive, setSugActive] = useState(0);
  const sugTimer = useRef(null);
  const sugAcceptedRef = useRef("");
  const [draft, setDraft] = useState("");

  const onInputChange = (v) => {
    setInput(v);
    clearTimeout(sugTimer.current);
    if (v.trim().length < 2 || v === sugAcceptedRef.current) { setSuggests([]); return; }
    sugTimer.current = setTimeout(() => {
      api.chatSuggest(v.trim())
        .then((r) => { setSuggests(r.data.suggestions || []); setSugActive(0); })
        .catch(() => setSuggests([]));
    }, 200);
  };

  const acceptSuggest = (text) => {
    sugAcceptedRef.current = text;
    setInput(text);
    setSuggests([]);
    inputRef.current?.focus();
  };

  // Load sessions list
  useEffect(() => {
    if (isOpen || isFullPage) {
      api.chatSessions().then((res) => setSessions(res.data?.sessions || [])).catch(() => {});
    }
  }, [isOpen, isFullPage]);

  // Auto scroll
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, draft]);

  const loadSession = useCallback(async (sid) => {
    try {
      const res = await api.chatHistory(sid);
      setMessages(res.data?.messages || []);
      setSessionId(sid);
      setShowSessions(false);
    } catch { }
  }, []);

  const newChat = () => {
    setMessages([]);
    setSessionId(null);
    setShowSessions(false);
    inputRef.current?.focus();
  };

  const saveMemory = async (idx, text) => {
    try {
      await api.chatSaveMemory(text);
      setMessages((prev) => prev.map((m, i) => (i === idx ? { ...m, suggest_memory: null, memory_saved: true } : m)));
      toast.success("Saved to memory");
    } catch {
      toast.error("Failed to save — try again");
    }
  };
  const dismissMemory = (idx) =>
    setMessages((prev) => prev.map((m, i) => (i === idx ? { ...m, suggest_memory: null } : m)));

  const downloadReport = async (reportId, fmt, title = "Report") => {
    try {
      const res = await api.chatReportDownload(reportId, fmt);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `Stancor_${title.replace(/[^a-zA-Z0-9]/g, "_")}.${fmt}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch { }
  };

  const sendMessage = async (overrideText) => {
    const text = (typeof overrideText === "string" ? overrideText : input).trim();
    if (!text || loading) return;
    setInput("");
    setMessages((prev) => [...prev, { role: "user", content: text }]);
    setLoading(true);
    setDraft("");
    setLoadingStatus("Analyzing your data\u2026");
    try {
      const res = await api.chatSend(text, sessionId);
      if (res.data?.session_id) setSessionId(res.data.session_id);
      const jobId = res.data?.job_id;
      if (!jobId) throw new Error("No job started");
      const started = Date.now();
      let waited = 0;
      while (Date.now() - started < 240000) {
        await new Promise((r) => setTimeout(r, 1500));
        waited = Math.round((Date.now() - started) / 1000);
        if (waited > 20) setLoadingStatus("Compiling report from live Odoo data\u2026 this can take a minute for big datasets");
        let jr;
        try { jr = await api.chatJob(jobId); } catch { continue; }
        const job = jr.data || {};
        if (job.status === "processing" && job.partial) setDraft(job.partial);
        if (job.status === "done") {
          const table = job.table || null;
          setMessages((prev) => [...prev, { role: "assistant", content: job.response || "No response", table,
            aliases_used: job.aliases_used || [], suggest_memory: job.suggest_memory || null }]);
          if (table?.auto_download) downloadReport(table.report_id, table.auto_download, table.title);
          return;
        }
        if (job.status === "error") {
          setMessages((prev) => [...prev, { role: "assistant", content: `Error: ${job.error || "Something went wrong"}` }]);
          return;
        }
      }
      setMessages((prev) => [...prev, { role: "assistant", content: "This is taking longer than expected. Open chat history in a minute — the answer will be saved there." }]);
    } catch (err) {
      setMessages((prev) => [...prev, { role: "assistant", content: `Error: ${err.message}` }]);
    } finally {
      setLoading(false);
      setDraft("");
      setLoadingStatus("");
    }
  };

  const exportToExcel = async () => {
    if (loading) return;
    const text = input.trim() || messages.filter((m) => m.role === "user").slice(-1)[0]?.content || "Pending orders summary";
    const hasFmt = /excel|xlsx|pdf|spreadsheet/i.test(text);
    sendMessage(hasFmt ? text : `${text} in excel`);
  };

  const handleKeyDown = (e) => {
    if (suggests.length) {
      if (e.key === "ArrowDown") { e.preventDefault(); setSugActive((a) => (a + 1) % suggests.length); return; }
      if (e.key === "ArrowUp") { e.preventDefault(); setSugActive((a) => (a - 1 + suggests.length) % suggests.length); return; }
      if (e.key === "Tab") { e.preventDefault(); acceptSuggest(suggests[sugActive].text); return; }
      if (e.key === "Escape") { setSuggests([]); return; }
    }
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); setSuggests([]); sendMessage(); }
  };

  const containerClass = isFullPage
    ? "flex flex-col h-[calc(100vh-48px)] md:h-screen bg-[#F8F9FB]"
    : "flex flex-col h-full";

  if (!isOpen && !isFullPage) return null;

  return (
    <div className={containerClass} data-testid="chat-panel">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-[#E2E5EA] px-4 py-3 bg-white shrink-0">
        <div className="flex items-center gap-2">
          <MessageSquare size={18} className="text-[#E32C17]" />
          <h3 className="text-sm font-bold uppercase tracking-wider text-[#1A1D21]">
            AI Analytics
          </h3>
        </div>
        <div className="flex items-center gap-1.5">
          <button onClick={newChat} className="p-1.5 rounded-sm hover:bg-[#F1F3F5] text-[#6B7280]" title="New chat">
            <Plus size={16} />
          </button>
          <button onClick={() => setShowSchedules(!showSchedules)}
            className={`p-1.5 rounded-sm hover:bg-[#F1F3F5] ${showSchedules ? "text-[#E32C17]" : "text-[#6B7280]"}`}
            title="Scheduled reports" data-testid="chat-schedules-toggle">
            <CalendarClock size={16} />
          </button>
          <button onClick={() => setShowSessions(!showSessions)}
            className="p-1.5 rounded-sm hover:bg-[#F1F3F5] text-[#6B7280]" title="History">
            <ChevronDown size={16} />
          </button>
          {!isFullPage && (
            <button onClick={onClose} className="p-1.5 rounded-sm hover:bg-[#F1F3F5] text-[#6B7280]">
              <X size={16} />
            </button>
          )}
        </div>
      </div>

      {/* Scheduled reports manager */}
      <ScheduledReports open={showSchedules} />

      {/* Session picker */}
      {showSessions && (
        <div className="border-b border-[#E2E5EA] bg-white max-h-[200px] overflow-y-auto">
          {sessions.length === 0 ? (
            <div className="p-3 text-xs text-[#9CA3AF] text-center">No previous chats</div>
          ) : sessions.map((s) => (
            <button key={s.session_id} onClick={() => loadSession(s.session_id)}
              className={`w-full text-left px-4 py-2 text-xs hover:bg-[#F1F3F5] border-b border-[#F1F3F5] ${
                sessionId === s.session_id ? "bg-[#FEF3C7]" : ""
              }`}>
              <div className="text-[#1A1D21] truncate">{s.preview}</div>
              <div className="text-[9px] text-[#9CA3AF]">{s.messages} messages</div>
            </button>
          ))}
        </div>
      )}

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
        {messages.length === 0 && (
          <div className="text-center py-8">
            <MessageSquare size={40} className="text-[#E2E5EA] mx-auto mb-3" />
            <p className="text-sm text-[#6B7280] font-medium">Ask about your data</p>
            <div className="mt-4 space-y-2">
              {[
                "Pending orders customer-wise in excel",
                "Stock status grade-wise",
                "Production status — what's on which machine?",
                "Material stuck in QC",
                "Open purchase orders vendor-wise in pdf",
                "Overdue orders grade-wise",
              ].map((q) => (
                <button key={q} onClick={() => { setInput(q); }}
                  className="block w-full text-left text-xs text-[#6B7280] bg-white border border-[#E2E5EA] rounded-sm px-3 py-2 hover:border-[#E32C17] hover:text-[#E32C17]"
                  style={{ transition: "border-color 0.15s" }}>
                  {q}
                </button>
              ))}
            </div>
          </div>
        )}

        {messages.map((m, i) => (
          <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
            <div className={`max-w-[85%] rounded-lg px-3 py-2 text-sm ${
              m.role === "user"
                ? "bg-[#E32C17] text-white rounded-br-none"
                : "bg-white border border-[#E2E5EA] text-[#1A1D21] rounded-bl-none"
            }`}>
              {m.role === "assistant" ? (
                <div>
                  {m.aliases_used?.length > 0 && (
                    <div className="flex flex-wrap items-center gap-1 mb-1.5" data-testid="chat-alias-hint">
                      <span className="text-[8px] font-bold uppercase text-[#9CA3AF]">Understood:</span>
                      {m.aliases_used.map((a) => (
                        <span key={a.short}
                          className="text-[9px] px-1.5 py-0.5 rounded-full bg-amber-50 border border-amber-200 text-amber-800 font-semibold">
                          {a.short.toUpperCase()} → {a.full}
                        </span>
                      ))}
                    </div>
                  )}
                  <div className="whitespace-pre-wrap text-xs leading-relaxed">
                    {m.content}
                  </div>
                  {m.table
                    ? <ChatTable table={m.table} onDownload={downloadReport} onSchedule={setScheduleFor} />
                    : <MdAnswerChart content={m.content} />}
                  {m.suggest_memory && !m.memory_saved && (
                    <div className="mt-2 flex items-center gap-2 flex-wrap bg-amber-50 border border-amber-200 rounded-sm px-2 py-1.5"
                      data-testid="chat-memory-suggest">
                      <span className="text-[10px] text-amber-800 font-semibold">
                        Looks like a correction — should I remember this permanently?
                      </span>
                      <button onClick={() => saveMemory(i, m.suggest_memory.text)} data-testid="chat-memory-yes"
                        className="text-[9px] px-2 py-0.5 rounded-sm font-bold bg-amber-600 text-white hover:bg-amber-700">
                        Yes, remember
                      </button>
                      <button onClick={() => dismissMemory(i)} data-testid="chat-memory-dismiss"
                        className="text-[9px] px-2 py-0.5 rounded-sm font-bold border border-amber-300 text-amber-700 hover:bg-amber-100">
                        Dismiss
                      </button>
                    </div>
                  )}
                  {m.memory_saved && (
                    <div className="mt-1.5 text-[9px] text-emerald-700 font-semibold" data-testid="chat-memory-saved">
                      ✓ Saved to memory — I'll use this in every future chat
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-xs">{m.content}</div>
              )}
            </div>
          </div>
        ))}

        {loading && draft && (
          <div className="flex justify-start" data-testid="chat-draft">
            <div className="max-w-[85%] bg-white border border-[#E2E5EA] rounded-lg px-4 py-3 rounded-bl-none">
              <div className="whitespace-pre-wrap text-xs leading-relaxed text-[#1A1D21]">
                {draft}
                <span className="inline-block w-1.5 h-3.5 bg-[#E32C17] ml-0.5 animate-pulse align-middle" />
              </div>
            </div>
          </div>
        )}
        {loading && !draft && (
          <div className="flex justify-start" data-testid="chat-loading">
            <div className="bg-white border border-[#E2E5EA] rounded-lg px-4 py-3 rounded-bl-none flex items-center gap-2">
              <Loader2 size={16} className="animate-spin text-[#E32C17]" />
              <span className="text-[10px] text-[#6B7280]">{loadingStatus || "Thinking\u2026"}</span>
            </div>
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      {scheduleFor && (
        <ScheduleDialog open onClose={() => setScheduleFor(null)}
          query={scheduleFor.query || scheduleFor.title}
          defaultTitle={scheduleFor.title}
          onSaved={() => setShowSchedules(true)} />
      )}

      {/* Input */}
      <div className="border-t border-[#E2E5EA] bg-white p-3 shrink-0 relative">
        {suggests.length > 0 && (
          <div className="absolute bottom-full left-3 right-3 mb-1 bg-white border border-[#E2E5EA] rounded-sm shadow-xl overflow-hidden z-30"
            data-testid="chat-suggest-box">
            <div className="text-[8px] font-bold uppercase text-[#9CA3AF] px-2 pt-1.5 pb-0.5 flex items-center justify-between">
              <span>Suggestions</span>
              <span className="normal-case font-normal">Tab or click to use · Esc to close</span>
            </div>
            {suggests.map((s, i) => {
              const Icon = { history: History, customer: Users, grade: Tag, template: Sparkles }[s.kind] || Sparkles;
              return (
                <button key={i} data-testid={`chat-suggest-item-${i}`}
                  onMouseDown={(e) => { e.preventDefault(); acceptSuggest(s.text); }}
                  onMouseEnter={() => setSugActive(i)}
                  className={`w-full text-left px-2 py-1.5 text-[11px] flex items-center gap-2 border-t border-[#F8F9FB] ${i === sugActive ? "bg-red-50" : "hover:bg-[#F8F9FB]"}`}>
                  <Icon size={11} className={s.kind === "history" ? "text-[#9CA3AF]" : "text-[#E32C17]"} />
                  <span className="truncate">{s.text}</span>
                  {s.kind === "history" && <span className="ml-auto text-[8px] text-[#9CA3AF] shrink-0">you asked before</span>}
                </button>
              );
            })}
          </div>
        )}
        <div className="flex items-end gap-2">
          <textarea ref={inputRef} value={input} onChange={(e) => onInputChange(e.target.value)}
            onKeyDown={handleKeyDown}
            onBlur={() => setTimeout(() => setSuggests([]), 200)}
            placeholder="Ask about production, dispatch, stock..."
            rows={1}
            className="flex-1 bg-[#F8F9FB] border border-[#E2E5EA] rounded-sm px-3 py-2 text-sm text-[#1A1D21] outline-none focus:border-[#E32C17] resize-none placeholder:text-[#9CA3AF]"
            style={{ minHeight: 38, maxHeight: 120 }}
            data-testid="chat-input" />
          <button onClick={exportToExcel} disabled={loading}
            className="p-2 rounded-sm bg-[#059669] hover:bg-[#047857] text-white disabled:opacity-40 shrink-0"
            title="Export as Excel" data-testid="chat-export-btn">
            <Download size={16} />
          </button>
          <button onClick={sendMessage} disabled={loading || !input.trim()}
            className="p-2 rounded-sm bg-[#E32C17] hover:bg-[#C41E10] text-white disabled:opacity-40 shrink-0"
            data-testid="chat-send-btn">
            <Send size={16} />
          </button>
        </div>
        <p className="text-[9px] text-[#9CA3AF] mt-1.5 text-center">
          GPT-5.2 &middot; Click <Download size={9} className="inline" /> to export report as Excel
        </p>
      </div>
    </div>
  );
}
